import { useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import {
  getListDocumentsQueryKey,
  useListDocuments,
} from "~/api/default/default";
import {
  Document,
  DocumentStatus,
  DocumentStatusCounts,
  ListDocuments200,
} from "~/api/model";
import { useGetCurrentUser } from "~/api/users/users";
import { fileMd5 } from "~/utils/fileMd5.ts";
import { useBatchKey } from "./useBatchKey";
import { useDocumentUpload } from "./useDocumentUpload";
import { useParentCase } from "./useParentCase.ts";

export type DocumentUpdateLog = {
  document: Document;
};
export type FileWithHash = File & { fileHash: string };
export type FileWithOptionalHash = File & { fileHash?: string };
const findDocumentStatusCounts = (
  documents: Document[] = [],
  status: DocumentStatus
) => {
  return documents.filter((doc) => doc.status === status).length || 0;
};

export const useDocumentsList = (organizationId: string, caseId: string) => {
  const { data, isLoading, isError } = useListDocuments(organizationId, caseId);
  const { data: currentUser } = useGetCurrentUser();
  const queryClient = useQueryClient();
  const [duplicateFiles, setDuplicateFiles] = useState<FileWithHash[]>([]);
  const [pendingFiles, setPendingFiles] = useState<FileWithHash[]>([]);
  const [showDuplicatesModal, setShowDuplicatesModal] = useState(false);

  const { sessionBatchKey } = useBatchKey({
    userId: currentUser?.id || "",
    documents: data?.documents,
  });

  const { isNested } = useParentCase();

  const {
    uploadDocuments,
    cancelDraftUpload,
    uploadProgress,
    queuedDocuments,
    setQueuedDocuments,
    // setProcessingDocuments,
    // setUploadProgress,
    hasWaitingDocuments,
    pendingUploads,
    activeUploads,
  } = useDocumentUpload({
    organizationId,
    caseId,
    sessionBatchKey,
  });

  const bulkUpdateDocuments = (updates: DocumentUpdateLog[]) => {
    const key = getListDocumentsQueryKey(organizationId, caseId);
    let shouldInvalidate = false;

    queryClient.setQueryData<ListDocuments200 | undefined>(key, (old) => {
      if (!old || !old.documents) {
        shouldInvalidate = true;
        return old;
      }

      const byId = new Map(updates.map((u) => [u.document.id, u.document]));
      const nextDocs = old.documents.map((d) => byId.get(d.id) ?? d);

      for (const u of updates) {
        if (
          u.document.status === DocumentStatus.COMPLETE ||
          u.document.status === DocumentStatus.ERROR
        ) {
          shouldInvalidate = true;
        }
      }

      return { ...old, documents: nextDocs };
    });

    if (shouldInvalidate) {
      queryClient.invalidateQueries({ queryKey: key });
    }
  };

  const isEmpty =
    (data?.documents?.length === 0 || data?.documents === undefined) &&
    queuedDocuments.length === 0;

  const hasOnlyArchivedDocuments = useMemo(() => {
    return isEmpty && isNested;
  }, [isEmpty, isNested]);

  const checkDuplicates = (files: FileWithHash[]): FileWithHash[] => {
    const existingFilenames = data?.documents?.map((doc) => doc.fileHash) || [];
    return files.filter((file) => existingFilenames.includes(file.fileHash));
  };

  const handleUpload = async (files: FileWithHash[]) => {
    // Don't process uploads for nested versions
    if (isNested) {
      return;
    }

    await queryClient.invalidateQueries({
      queryKey: getListDocumentsQueryKey(organizationId, caseId),
    });

    for (let i = 0; i < files.length; i += 1) {
      files[i].fileHash = await fileMd5(files[i]);
    }

    const duplicates: FileWithHash[] = checkDuplicates(files);

    if (duplicates.length > 0) {
      // setDuplicateFiles(duplicates.map((f) => f.fileHash));
      setDuplicateFiles(duplicates);
      setPendingFiles(files);
      setShowDuplicatesModal(true);
      return;
    }
    const newQueuedDocs: (Document & { uploadId?: string })[] = files.map(
      (file) => ({
        filename: file.name,
        fileHash: file.fileHash,
        status: DocumentStatus.DRAFT,
        createdAt: new Date().toISOString(),
        authorId: currentUser!.id,
        author: currentUser!,
        folderId: caseId,
        previewUrl: "",
        size: file.size,
        type: file.type,
        contentType: file.type,
        downloadCount: 0,
        id: `queued-${file.name}-${Date.now()}`,
        s3Key: "",
        updatedAt: new Date().toISOString(),
        url: "",
        batchKey: sessionBatchKey,
        isStructured: false,
      })
    );

    setQueuedDocuments((prev) => {
      const filteredPrev = prev.filter(
        (doc) => !files.some((file) => file.name === doc.filename)
      );
      return [...filteredPrev, ...newQueuedDocs];
    });

    // await
    uploadDocuments(files as FileWithHash[]);
  };

  const handleDuplicateConfirm = async () => {
    setShowDuplicatesModal(false);

    await uploadDocuments(pendingFiles as FileWithHash[]);
    setPendingFiles([]);
    setDuplicateFiles([]);
  };

  const handleContinueWithoutDuplicates = async () => {
    setShowDuplicatesModal(false);
    const dups = duplicateFiles.map((d) => d.fileHash);
    const nonDuplicates = pendingFiles.filter((p) => {
      return dups.indexOf(p.fileHash) == -1;
    });
    setQueuedDocuments(
      nonDuplicates.map((file) => {
        return {
          filename: file.name,
          fileHash: file.fileHash,
          status: DocumentStatus.DRAFT,
          createdAt: new Date().toISOString(),
          authorId: currentUser!.id,
          author: currentUser!,
          folderId: caseId,
          previewUrl: "",
          size: file.size,
          type: file.type,
          contentType: file.type,
          downloadCount: 0,
          id: `queued-${file.name}-${Date.now()}`,
          s3Key: "",
          updatedAt: new Date().toISOString(),
          url: "",
          batchKey: sessionBatchKey,
          isStructured: false,
        };
      })
    );
    await uploadDocuments(nonDuplicates as (File & { fileHash: string })[]);
    setPendingFiles([]);
    setDuplicateFiles([]);
  };

  const handleDuplicateCancel = () => {
    setShowDuplicatesModal(false);
    setPendingFiles([]);
    setDuplicateFiles([]);
  };

  const counts: DocumentStatusCounts = useMemo(() => {
    return {
      fileCount: data?.documents?.length || 0,
      processing_count: findDocumentStatusCounts(
        data?.documents,
        DocumentStatus.PROCESSING
      ),
      error_count: findDocumentStatusCounts(
        data?.documents,
        DocumentStatus.ERROR
      ),
      complete_count: findDocumentStatusCounts(
        data?.documents,
        DocumentStatus.COMPLETE
      ),
      draft_count: findDocumentStatusCounts(
        data?.documents,
        DocumentStatus.DRAFT
      ),
      uploaded_count: findDocumentStatusCounts(
        data?.documents,
        DocumentStatus.UPLOADED
      ),
    };
  }, [data?.documents]);

  // Combine actual documents with queued documents for display
  const allDocuments = useMemo(() => {
    const apiDocs = data?.documents || [];
    const apiDocsMap = new Map(apiDocs.map((doc) => [doc.uploadId, doc]));
    const result: Document[] = [];

    queuedDocuments.forEach((queuedDoc) => {
      const apiDoc = apiDocsMap.get(queuedDoc.uploadId);
      if (apiDoc) {
        result.push(apiDoc);
        apiDocsMap.delete(queuedDoc.uploadId);
      } else {
        result.push({
          ...queuedDoc,
          id: `queued-${queuedDoc.filename}-${Date.now()}`,
          projectId: caseId,
          organizationId,
          authorId: currentUser?.id || "1",
          createdAt: queuedDoc.createdAt,
          updatedAt: queuedDoc.createdAt,
          status: DocumentStatus.DRAFT,
          s3Key: "",
          downloadCount: 0,
          batchKey: queuedDoc.batchKey,
        } as Document);
      }
    });

    apiDocsMap.forEach((doc) => result.push(doc));

    return result.sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  }, [
    queuedDocuments,
    data?.documents,
    caseId,
    organizationId,
    currentUser?.id,
  ]);

  const totalPages = useMemo(() => {
    return allDocuments.reduce((acc, doc) => acc + (doc.pageCount || 0), 0);
  }, [allDocuments]);

  const allFilesNotProcessed = useMemo(() => {
    return allDocuments.some((doc) => doc.status !== DocumentStatus.COMPLETE);
  }, [allDocuments]);

  return {
    documents: allDocuments,
    isLoading,
    isError,
    uploadDocuments,
    isEmpty,
    hasOnlyArchivedDocuments,
    bulkUpdateDocuments,
    handleUpload,
    duplicateFiles,
    pendingFiles,
    showDuplicatesModal,
    handleDuplicateConfirm,
    handleContinueWithoutDuplicates,
    handleDuplicateCancel,
    uploadProgress,
    pendingUploads,
    activeUploads,
    hasWaitingDocuments,
    counts,
    queuedDocuments,
    totalPages,
    allFilesNotProcessed,
    cancelDraftUpload,
  };
};
